import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { delegationService } from '../services/delegationService';
import { assemblyService } from '../services/assemblyService';
import { Assembly } from '../types/assembly';
import { notifyError, notifySuccess } from '../utils/notificationCenter';
import { formatAssemblyCalendarDateEs, formatAssemblyTimeEs } from '../utils/assemblyDate';
import { AlertTriangle } from 'lucide-react';

const DelegatePage: React.FC = () => {
  const { assemblyId } = useParams<{ assemblyId: string }>();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const userId = searchParams.get('uid');
  
  const [assembly, setAssembly] = useState<Assembly | null>(null);
  const [delegateEmail, setDelegateEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [canDelegate, setCanDelegate] = useState<boolean | null>(null);

  useEffect(() => {
    if (!assemblyId || !token || !userId) {
      notifyError('Enlace inválido o expirado');
      setError('Enlace inválido o expirado');
      return;
    }

    // Cargar información de la asamblea (sin autenticación, es una URL pública)
    assemblyService.getAssemblyByIdPublic(assemblyId)
      .then(setAssembly)
      .catch(() => {
        notifyError('No se pude cargar la asamblea');
        setError('No se pude cargar la asamblea');
      });

    // Validar si el usuario puede delegar
    fetch(`${process.env.REACT_APP_API_URL}/delegations/validate/${userId}/${assemblyId}`)
      .then(res => res.json())
      .then(data => {
        if (data.success === false) {
          // Si la validación falla, mostrar error
          setCanDelegate(false);
          const errorMsg = data.message || 'No puedes delegar tu voto en esta asamblea';
          notifyError(errorMsg);
          setError(errorMsg);
        } else {
          setCanDelegate(data.canDelegate);
          if (!data.canDelegate) {
            const errorMsg = 'No puedes delegar votos después de haber recibido delegaciones. Esto previene la acumulación excesiva de poder de voto.';
            notifyError(errorMsg);
            setError(errorMsg);
          }
        }
      })
      .catch(err => {
        console.error('Error al validar delegación:', err);
        // Si hay error en la validación, permitir continuar pero mostrar advertencia
        setCanDelegate(null);
      });
  }, [assemblyId, token, userId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!delegateEmail.trim()) {
      notifyError('Ingresa el email del delegado');
      return;
    }

    if (!assemblyId || !token || !userId) {
      notifyError('Datos de delegación inválidos');
      return;
    }

    setLoading(true);

    try {
      await delegationService.createDelegation({
        assemblyId,
        delegatorToken: token,
        delegateEmail: delegateEmail.trim(),
        userId,
      });
      
      notifySuccess('¡Solicitud enviada!', 'Delegación');
      setSuccess(true);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al crear la delegación';
      notifyError(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Solicitud Enviada!
            </h2>
            <p className="text-gray-600 mb-6">
              Se envió un email a <strong>{delegateEmail}</strong> para que acepte votar por ti.
            </p>
            <p className="text-sm text-gray-500">
              Recibirás una notificación cuando acepte o rechace tu solicitud.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            🔄 Delegar mi Voto
          </h1>
          <p className="text-gray-600">
            {assembly ? assembly.title : 'Cargando...'}
          </p>
        </div>

        {assembly && canDelegate !== false && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">
                Detalles de la Asamblea
              </h3>
              <div className="text-sm text-blue-800 space-y-1">
                <p>📅 {formatAssemblyCalendarDateEs(assembly.scheduledDate, assembly.scheduledDate)}</p>
                <p>🕐 {formatAssemblyTimeEs(assembly.startTime, 'Por confirmar')}</p>
                <p>📍 {assembly.isVirtual ? 'Virtual' : assembly.location}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="delegateEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Email de quien votará por ti
                </label>
                <input
                  type="email"
                  id="delegateEmail"
                  value={delegateEmail}
                  onChange={(e) => setDelegateEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ejemplo@email.com"
                  required
                  disabled={loading}
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-yellow-900 mb-2">
                  <span className="inline-flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-800" aria-hidden="true" />
                    <span>Importante</span>
                  </span>
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• No podrás votar en esta asamblea</li>
                  <li>• El delegado debe ser del mismo conjunto</li>
                  <li>• Puedes revocar antes o durante la asamblea</li>
                  <li>• El delegado debe aceptar tu poder</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 px-4 rounded-md hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? 'Enviando...' : 'Delegar y firmar'}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            DHAssembly - Sistema de Gestión de Asambleas
          </p>
        </div>
      </div>
    </div>
  );
};

export default DelegatePage;
